// src/components/opportunity/OpportunityTodoItemRow.tsx

import { useEffect, useState } from "react";
import type { OppTodoItem } from "@/lib/opportunity/oppTodoItemTypes";
import { isOppTodoItemOpen } from "@/lib/opportunity/oppTodoItemTypes";
import type { UpsertOppTodoItemPayload } from "@/lib/opportunity/oppTodoItemClient";

type Props = {
  item: OppTodoItem;
  editable: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onToggle: (id: string, completed: boolean) => Promise<{ ok: boolean; error?: string }>;
  onSave: (payload: UpsertOppTodoItemPayload) => Promise<{ ok: boolean; error?: string }>;
  onDelete: (id: string) => Promise<{ ok: boolean; error?: string }>;
  onMove: (direction: "up" | "down") => void;
};

export function OpportunityTodoItemRow(props: Props) {
  const { item, editable, canMoveUp, canMoveDown, onToggle, onSave, onDelete, onMove } = props;
  const open = isOppTodoItemOpen(item);
  const [label, setLabel] = useState(item.label);
  const [dueDate, setDueDate] = useState(item.due_date ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLabel(item.label);
    setDueDate(item.due_date ?? "");
  }, [item.id, item.label, item.due_date]);

  async function handleToggle() {
    if (!editable) return;
    setBusy(true);
    setError(null);
    const r = await onToggle(item.id, open);
    setBusy(false);
    if (!r.ok) setError(r.error ?? "Could not update item.");
  }

  async function handleSave() {
    if (!editable) return;
    setBusy(true);
    setError(null);
    const r = await onSave({
      id: item.id,
      label,
      sort_order: item.sort_order,
      due_date: dueDate.trim() === "" ? null : dueDate,
    });
    setBusy(false);
    if (!r.ok) setError(r.error ?? "Could not save item.");
  }

  async function handleDelete() {
    if (!editable) return;
    if (!window.confirm("Remove this todo step?")) return;
    setBusy(true);
    setError(null);
    const r = await onDelete(item.id);
    setBusy(false);
    if (!r.ok) setError(r.error ?? "Could not delete item.");
  }

  return (
    <div
      className={
        "rounded-xl border px-3 py-2 " +
        (open
          ? "border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900"
          : "border-gray-100 bg-gray-50/80 opacity-80 dark:border-zinc-800 dark:bg-zinc-900/50")
      }
    >
      <div className="flex flex-wrap items-start gap-2">
        <input
          type="checkbox"
          checked={!open}
          disabled={!editable || busy}
          onChange={() => void handleToggle()}
          className="mt-2 h-4 w-4 rounded border-gray-300"
          aria-label={`Mark ${item.label} complete`}
        />

        {editable ? (
          <div className="flex shrink-0 flex-col gap-0.5 pt-1">
            <button
              type="button"
              disabled={!canMoveUp || busy}
              onClick={() => onMove("up")}
              className="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] disabled:opacity-40 dark:border-zinc-600"
              aria-label="Move up"
            >
              ↑
            </button>
            <button
              type="button"
              disabled={!canMoveDown || busy}
              onClick={() => onMove("down")}
              className="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] disabled:opacity-40 dark:border-zinc-600"
              aria-label="Move down"
            >
              ↓
            </button>
          </div>
        ) : null}

        <div className="min-w-0 flex-1 space-y-2">
          {editable ? (
            <>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              />
              <label className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-zinc-300">
                <span className="shrink-0">Due date</span>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-zinc-600 dark:bg-zinc-900"
                />
              </label>
            </>
          ) : (
            <>
              <p
                className={
                  "text-sm " +
                  (open
                    ? "text-gray-900 dark:text-zinc-100"
                    : "text-gray-500 line-through dark:text-zinc-400")
                }
              >
                {item.label}
              </p>
              {item.due_date ? (
                <p className="text-[11px] text-gray-500 dark:text-zinc-400">Due {item.due_date}</p>
              ) : null}
            </>
          )}
        </div>

        {editable ? (
          <div className="flex shrink-0 flex-col gap-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleSave()}
              className="rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleDelete()}
              className="rounded-lg border border-red-200 px-2.5 py-1 text-[11px] text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-950/40"
            >
              Remove
            </button>
          </div>
        ) : null}
      </div>
      {error ? <p className="mt-2 text-[11px] text-red-700 dark:text-red-200">{error}</p> : null}
    </div>
  );
}
